import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import ReactSelect, { ActionMeta, Styles } from 'react-select';
import Creatable, { Props as CreatableProps } from 'react-select/creatable';
import { useTheme } from 'uinix-ui';

import { InputValue } from '../../types/InputValue';
import { Nullable } from '../../types/Nullable';
import { Option } from '../../types/Option';
import { ValuesMap } from '../../types/ValuesMap';
import { fromValuesMap } from '../../utils/fromValuesMap';
import { testHasTextMatch } from '../../utils/testHasTextMatch';
import { toValuesMap } from '../../utils/toValuesMap';
import { typedMemo } from '../../utils/typedMemo';
import { ClearIndicator } from './components/ClearIndicator';
import { Control } from './components/Control';
import { DropdownIndicator } from './components/DropdownIndicator';
import { Group } from './components/Group';
import { GroupHeading } from './components/GroupHeading';
import { IndicatorsContainer } from './components/IndicatorsContainer';
import { IndicatorSeparator } from './components/IndicatorSeparator';
import { Input as SelectInput } from './components/Input';
import { LoadingIndicator } from './components/LoadingIndicator';
import { Menu } from './components/Menu';
import { MultiValue } from './components/MultiValue';
import { MultiValueContainer } from './components/MultiValueContainer';
import { MultiValueRemove } from './components/MultiValueRemove';
import { Option as SelectOption, toValue } from './components/Option';
import { Placeholder } from './components/Placeholder';
import { SingleValue } from './components/SingleValue';
import { ValueContainer } from './components/ValueContainer';
import { arrayMove, getSortProps } from './sortable-utils';
import { SelectProps } from './types';
import {
  filterGroupedOptions,
  findOption,
  getOptionSelectAll,
  getStyles,
  sortOptions,
  testIsOptionDisabled,
  testIsOptionsGrouped,
} from './utils';

const selectComponents = {
  ClearIndicator,
  Control,
  DropdownIndicator,
  Group,
  GroupHeading,
  IndicatorsContainer,
  IndicatorSeparator,
  Input: SelectInput,
  LoadingIndicator,
  Menu,
  MultiValue,
  MultiValueContainer,
  MultiValueRemove,
  Option: SelectOption,
  Placeholder,
  SingleValue,
  ValueContainer,
};

/**
 * `Select` provides a common controlled component for managing user selections.
 *
 * It implements the `Input` and `Option` interfaces with generic value signature set by the domain.
 *
 * It supports multi-selection when `isMulti` is enabled.
 *
 * It supports an embedded rendering mode when `isEmbedded` is enabled.  This causes the select to be embedded in its parents container with some behavioral changes.
 *
 * Constrained configurations (e.g. `enableSearchIcon`, `enableValueContainer`, `isSearchable`) ensure `Select` is used in consistent ways that do not deviate from design specs.
 *
 * `Select` also supports the ability to create new options.  Specify the `onAddOption` callback and control the `options` prop to leverage this feature.  Note that the `onAddOption` will add the newly created option to the selected values and run the provided value `onChange` handler.
 *
 * `Select` is implemented by lightly wrapping the react-select library (see https://react-select.com/).  We implement customoizations by overriding vendor components in the ./components folder and avoid customizations using the vendor `styles` API.
 */
export const Select = typedMemo(
  <V extends unknown, M extends boolean>({
    autoFocus,
    disabled,
    enableControl = true,
    enablePortal = false,
    enableSearchIcon,
    enableSelectAll = true,
    enableValueContainer = true,
    enableErrorMessage,
    error,
    filterOption = () => true, // no filtering
    footerAction,
    id,
    isClearable = true,
    isEmbedded,
    isLoading,
    isMenuOpen,
    isMulti,
    isSearchable = true,
    limit,
    maxWidth,
    menuHeader,
    menuMaxHeight,
    menuPlacement = 'auto',
    name,
    noOptionsMessage,
    readOnly,
    selectAllCount,
    options: initialOptions,
    placeholder,
    value: initialValue,
    width,
    onAddOption,
    onBlur,
    onChange,
    onFocus,
    onFilterOption,
    onMount,
    onSearch,
    enableOptionIndicator = isMulti,
    enableSorting = isEmbedded,
    asyncValue,
    shouldAllowInvalidValue = true,
  }: SelectProps<V, M>) => {
    const [search, setSearch] = useState<Nullable<string>>(null);
    const theme = useTheme();
    const intl = useIntl();
    const isCreatable = !!onAddOption;

    const SELECT_ALL_OPTION = getOptionSelectAll(intl);

    const value = useMemo(() => {
      // TODO EKP-8607: implement this formally, placeholder implementation for supporting new interface (props.asyncValue)
      return isMulti && asyncValue
        ? fromValuesMap<V>(asyncValue.selectedValuesMap)
        : (initialValue ?? null);
    }, [isMulti, asyncValue, initialValue]);

    const options = useMemo(() => {
      const sortedOptions =
        enableSorting && !testIsOptionsGrouped(initialOptions)
          ? sortOptions(initialOptions, value)
          : initialOptions;
      return onFilterOption
        ? filterGroupedOptions(sortedOptions, onFilterOption)
        : sortedOptions;
    }, [enableSorting, initialOptions, onFilterOption, value]);

    // TODO: deprecate `shouldNotControlFiltering` so filtering can be merged with `props.onFilterOption = defaultFilterOption`
    const shouldNotControlFiltering = !search || onSearch;
    const filteredOptions = useMemo(() => {
      if (shouldNotControlFiltering) {
        return limit ? options.slice(0, limit) : options;
      }
      const defaultFilterOption = (option: Option<V>) =>
        testHasTextMatch(option.label, search) ||
        testHasTextMatch(option.description || '', search);
      return limit
        ? filterGroupedOptions(options, defaultFilterOption).slice(0, limit)
        : filterGroupedOptions(options, defaultFilterOption);
    }, [shouldNotControlFiltering, limit, options, search]);

    // option-based value used by react-select
    const optionValue: Option<V> | Option<V>[] | null = useMemo(() => {
      if (value === null) {
        return null;
      } else if (isMulti && Array.isArray(value)) {
        return value.map(
          findOption(options, shouldAllowInvalidValue, isLoading),
        );
      } else {
        return findOption(
          options,
          shouldAllowInvalidValue,
          isLoading,
        )(value as V);
      }
    }, [isLoading, isMulti, options, shouldAllowInvalidValue, value]);

    const isAllSelected = useMemo(() => {
      if (isMulti) {
        const valueSet = new Set(value as V[]);
        return filteredOptions.every((o) => valueSet.has(o.value));
      }
      return false;
    }, [isMulti, value, filteredOptions]);

    // This state is used to "snapshot" the above search state whenever user checks the Select All option in async mode.
    // The main reason this is a string not a boolean is that user can search a subset of options and select the entire subset. Using a boolean won't give us enough information as soon as user changes the search.
    // As a result, it is the center piece of the async select all handling and used in these different ways:
    //   1. determine the checkbox status of select all - checked, unchecked, indeterminate
    //   2. determine the onChange updatedAsyncValue payload when an option is checked/unchecked
    // Note: the initial state - null, is purposely designed to differentiate from an empty string ''.
    // That is because user can Select All without a search, and we need the '' to capture that state, which is different than the state that, for example, user unchecks it subsequently.
    const [asyncSearch, setAsyncSearch] = useState<Nullable<string>>(null);

    useEffect(() => {
      const initAsyncSearch = asyncValue?.search;
      if (initAsyncSearch) {
        setSearch(initAsyncSearch);
      }
      if (asyncValue?.isAllSelected) {
        setAsyncSearch(initAsyncSearch ?? '');
      }
      if (onMount) {
        onMount(initAsyncSearch || '');
      }
    }, []);

    const isIndeterminate =
      enableSelectAll &&
      isMulti &&
      !isAllSelected &&
      value &&
      (value as V[]).length > 0;
    // enableSelectAll && !isAllSelected && isMulti && (value as V[]).length > 0;

    // Since many of the considerations between async and non-async are quite different, it makes sense (to me at least) to fork the code paths (for maintainability's sake).
    // We may change how we derive this flag but this should suffice for a while.
    const isAsync = !!onSearch;

    const sortProps = enableSorting
      ? getSortProps(({ oldIndex, newIndex }) => {
          const updatedValues = arrayMove(
            value as V[],
            oldIndex,
            newIndex,
          ) as InputValue<V, M>;
          onChange(updatedValues, asyncValue, { action: 'sort-option' });
        })
      : undefined;

    const handleSearch = (updatedSearch: string) => {
      onSearch?.(updatedSearch);
      setSearch(updatedSearch);
    };

    const ReactSelectComponent = (
      isCreatable ? Creatable : ReactSelect
    ) as React.ComponentType<CreatableProps<Option<V>, boolean>>;

    /**
     *
     * This logic is fairly complex but the goals are to correctly update both `value` and `search` state given the configuration of `Select` (e.g. `isMulti`, `isAsync`) and the action meta that has fired.
     */
    const handleUpdatedValues = (
      incomingValues: V | V[] | null,
      meta: ActionMeta<Option<V, unknown>>,
    ) => {
      let updatedAsyncValue;
      let updatedValue;
      // always reset search on this action
      if (meta.action === 'clear') {
        handleSearch('');
      }
      // empty value
      if (incomingValues === null) {
        updatedValue = null;
      }
      // multi-value
      else if (isMulti && Array.isArray(incomingValues)) {
        if (!isEmbedded) {
          // only clear search in non-embedded mode
          handleSearch('');
        }
        if (!isAsync) {
          if (
            meta.action === 'select-option' &&
            meta.option!.value === SELECT_ALL_OPTION.value &&
            search
          ) {
            updatedValue = filteredOptions.map(toValue);
          } else if (
            meta.action === 'deselect-option' &&
            meta.option!.value === SELECT_ALL_OPTION.value &&
            search
          ) {
            // uncheck a subset of selected values via select all
            const filteredOptionSet = new Set(
              filteredOptions.map((o) => o.value),
            );
            updatedValue = (value as V[]).filter(
              (v) => !filteredOptionSet.has(v),
            );
          } else {
            updatedValue = incomingValues;
          }
          updatedAsyncValue = null;
        } else {
          // async mode with select all and search - where the fun begins
          if (meta.action === 'clear') {
            // user clicks the clear icon in the search bar to remove all chips
            updatedValue = incomingValues;
            setAsyncSearch(null);
            updatedAsyncValue = {
              isAllSelected: false,
              length: updatedValue.length,
              search: asyncSearch,
              selectedValuesMap: toValuesMap<V>(updatedValue),
            };
          } else {
            // user is clicking on an option
            updatedValue = incomingValues;

            if (search === asyncSearch) {
              let isAllSelected = asyncValue?.isAllSelected ?? false;
              let selectedValuesMap = {
                ...(asyncValue?.selectedValuesMap || ({} as ValuesMap<V>)),
              };

              // This is important for upstream (ie. backend) to derive the blacklist.
              // For example, user selects all 1000 options except one.
              if (meta.action === 'deselect-option') {
                if (meta.option!.value === SELECT_ALL_OPTION.value) {
                  isAllSelected = false;
                  selectedValuesMap = {} as ValuesMap<V>;
                } else {
                  selectedValuesMap[meta.option!.value] = false;
                }
              } else if (meta.action === 'remove-value') {
                // user clicks on the remove icon on a value chip
                selectedValuesMap[meta.removedValue.value] = false;
              } else if (meta.action === 'pop-value') {
                // user use keyboard to backspace on the search input
                selectedValuesMap[meta.removedValue.value] = false;
              } else if (meta.action === 'select-option') {
                if (meta.option!.value === SELECT_ALL_OPTION.value) {
                  isAllSelected = true;
                  selectedValuesMap = toValuesMap<V>(options.map(toValue));
                } else {
                  selectedValuesMap[meta.option!.value] = true;
                }
              }

              // We always know user wants to select all at this moment because search === asyncSearch
              updatedAsyncValue = {
                isAllSelected,
                length: updatedValue.length,
                search: asyncSearch,
                selectedValuesMap,
              };
            } else {
              // This is when user updated search, and checks or unchecks an option.
              // There are potentially some fine tunings we can do here, but here is what we agreed on we'll do for now:
              // Regardless of if user previously selected all (with or without search), we will clear it. See test case A1.
              setAsyncSearch(null);
              updatedAsyncValue = {
                isAllSelected:
                  (meta.action === 'select-option' &&
                    meta.option!.value === SELECT_ALL_OPTION.value) ||
                  updatedValue.length === filteredOptions.length,
                length: updatedValue.length,
                search,
                selectedValuesMap: toValuesMap<V>(updatedValue),
              };
            }
          }
        }
      }
      // single-value
      else {
        updatedValue = incomingValues;
        handleSearch('');
      }

      const actionMeta = {
        action: meta.action,
      };

      onChange(updatedValue as InputValue<V, M>, updatedAsyncValue, actionMeta);
    };

    // react-select vendor lacks formal support of handling `readOnly` (e.g. disabling updating value)
    // we handle readOnly and disabled UI similarly in various UI features
    const isDisabledOrReadOnly = disabled || readOnly;
    const isWidthRelative = width && ['100%', '50%'].includes(width);

    const menuDefaultMaxHeight = theme.sizes[
      'select.menu.max-height'
    ] as number;

    return (
      <ReactSelectComponent
        autoFocus={autoFocus}
        components={selectComponents as any} // figure out components type
        closeMenuOnSelect={!isMulti}
        footerAction={footerAction}
        hideSelectedOptions={false}
        id={id}
        inputValue={search ?? ''}
        isClearable={isClearable}
        isDisabled={isDisabledOrReadOnly}
        isLoading={isLoading}
        isOptionDisabled={testIsOptionDisabled}
        isMulti={isMulti}
        isSearchable={isSearchable}
        menuIsOpen={isEmbedded || isMenuOpen}
        menuPosition={isEmbedded || enablePortal ? undefined : 'fixed'}
        name={name}
        noOptionsMessage={() =>
          noOptionsMessage ??
          intl.formatMessage({
            id: 'Select.noOptionsMessage',
            defaultMessage: 'No options',
            description:
              'Message shown when no options are available in the select dropdown',
          })
        }
        options={filteredOptions}
        filterOption={(option) => filterOption(option as Option<V>)}
        placeholder={placeholder}
        readOnly={readOnly}
        styles={
          getStyles({
            isEmbedded,
            isWidthRelative,
            maxWidth,
          }) as Styles<Option<V>, boolean>
        }
        // Maximum height of the menu before scrolling
        maxMenuHeight={menuMaxHeight ?? menuDefaultMaxHeight}
        // From the docs: Minimum height of the menu before flipping
        // it does not actually set the min-height of the menu, its used to determine the amount of space needed before flipping from bottom to top
        minMenuHeight={menuMaxHeight ?? menuDefaultMaxHeight}
        value={optionValue}
        onChange={(updatedOption, meta) => {
          const updatedValue = isMulti
            ? (updatedOption as Option<V>[]).map(toValue)
            : ((updatedOption as Option<V>)?.value ?? null);
          handleUpdatedValues(updatedValue, meta);
        }}
        onCreateOption={
          onAddOption
            ? (newValue) => {
                const isNewValue = !options.some(
                  (option) => option.value === newValue,
                );
                if (isNewValue) {
                  onAddOption({ value: newValue as V, label: newValue });
                  const updatedValue = isMulti
                    ? ([...((value as V[]) || []), newValue] as V[])
                    : (newValue as V);
                  handleUpdatedValues(updatedValue, {
                    action: 'create-option',
                  });
                }
              }
            : undefined
        }
        onBlur={onBlur}
        onFocus={onFocus}
        onInputChange={(updatedSearch: string, meta) => {
          if (meta.action === 'input-change') {
            handleSearch(updatedSearch);
          }
        }}
        // non-react-select props
        asyncValue={asyncValue}
        enableErrorMessage={enableErrorMessage}
        enableControl={enableControl}
        enableOptionIndicator={enableOptionIndicator}
        enableSearchIcon={enableSearchIcon}
        enableSelectAll={enableSelectAll}
        enableValueContainer={isEmbedded ? false : enableValueContainer}
        error={error}
        inputWidth={width} // do not assign to "width" prop (would affect the vendor's behavior)
        isAllSelected={isAsync ? asyncSearch !== null : isAllSelected}
        isEmbedded={isEmbedded}
        isIndeterminate={
          asyncSearch
            ? search !== asyncSearch || isIndeterminate
            : isIndeterminate
        }
        menuHeader={menuHeader}
        menuPlacement={menuPlacement}
        selectAllCount={selectAllCount}
        sortProps={sortProps}
        role={isSearchable ? 'combobox' : 'listbox'}
        aria-label={name}
      />
    );
  },
);
